/**
 * 画面遷移
 */
import { Mp9010Display } from '../../../shared/data-save/usage-history.entity';

/** 別タブで MP9010 を開くときに一時保存するキー（同一オリジンの localStorage） */
const MP9010_NAV_STORAGE_KEY = 'xbh.mp9010.navigationState';

/** 呼び出し元から history.state で渡す MP9010 パラメータ */
export interface Mp9010NavigationState extends Partial<Mp9010Display> {
  /** ホスト通信エラーコード */
  errorCode?: string | null;
  /** エラーメッセージ */
  errorMessage?: string | null;
}

/** エラー画面遷移のオプション */
export interface NavigateToErrorPageOptions {
  /** true のとき別タブでエラー画面を開く（元画面は維持） */
  openInNewTab?: boolean;
}

/**
 * エラー画面へ遷移する。
 * 業務エラー時は第1引数に API レスポンスを渡し、resInfo.errorContents の errorCodeHost / errorMsg を載せる。
 * HTTP異常時は第1引数に null を渡し、ホスト通信エラーコード・エラーメッセージを空にする。
 * @param error 業務エラー時の API レスポンス。HTTP異常時は null
 * @param router Angular Router
 * @param display 画面タイトル・ステップバー（呼び出し元で指定。エミュレーション用には businessData.errorDisplay にも同じ値を保存すること）
 * @param options openInNewTab 時は別タブでエラー画面を表示する
 */
export function navigateToErrorPage(
  error: unknown,
  router: { navigate: (commands: unknown[], extras?: { state?: Mp9010NavigationState }) => void },
  display?: Mp9010Display,
  options?: NavigateToErrorPageOptions,
): void {
  const state = buildMp9010NavigationState(error, display);
  if (options?.openInNewTab) {
    persistMp9010NavigationState(state);
    openRouteInNewTab('/mp-app/error');
    return;
  }
  router.navigate(['/mp-app/error'], { state });
}

/**
 * 別タブ起動時に保存した MP9010 パラメータを取得し、ストレージから削除する
 */
export function consumeMp9010NavigationState(): Mp9010NavigationState | null {
  try {
    const raw = localStorage.getItem(MP9010_NAV_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    localStorage.removeItem(MP9010_NAV_STORAGE_KEY);
    return JSON.parse(raw) as Mp9010NavigationState;
  } catch {
    return null;
  }
}

/**
 * history.state に MP9010 用パラメータが含まれるか
 */
export function hasMp9010NavigationState(state: Mp9010NavigationState | null | undefined): boolean {
  if (!state) {
    return false;
  }
  return 'screenTitle' in state || 'wizardDisplayType' in state || 'wizardLevel' in state || 'errorCode' in state || 'errorMessage' in state;
}

/**
 * ページを別タブで開く
 * @param url 開くページのURL
 */
export function openInNewTab(url: string): void {
  window.open(url, '_blank', 'width=946,height=604,resizable=yes,scrollbars=yes,noopener,noreferrer');
}

/**
 * アプリ内ルートを別タブで開く
 * @param path ルートパス（例: /mp0011）
 */
export function openRouteInNewTab(path: string): void {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  let finalPath = normalizedPath;
  const currentPathname = window.location.pathname;
  const targetIndex = currentPathname.indexOf('/mp-app');
  if (targetIndex > 0) {
    const dynamicPrefix = currentPathname.substring(0, targetIndex);

    if (!normalizedPath.startsWith(dynamicPrefix)) {
      finalPath = `${dynamicPrefix}${normalizedPath}`;
    }
  }
  openInNewTab(`${window.location.origin}${finalPath}`);
}

function persistMp9010NavigationState(state: Mp9010NavigationState): void {
  try {
    localStorage.setItem(MP9010_NAV_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ストレージ使用不可時はクエリ無しで開き、MP9010 側の既定表示に任せる
  }
}

function buildMp9010NavigationState(error: unknown, display?: Mp9010Display): Mp9010NavigationState {
  if (error == null) {
    return { ...display, errorCode: null, errorMessage: null };
  }

  const errorRecord = error as { resInfo?: { errorContents?: unknown } };
  const errorContents = errorRecord?.resInfo?.errorContents;
  const firstError = (Array.isArray(errorContents) ? errorContents[0] : errorContents) as { errorCodeHost?: string | null; errorMsg?: string | null } | undefined;
  return {
    ...display,
    errorCode: firstError?.errorCodeHost ?? null,
    errorMessage: firstError?.errorMsg ?? null,
  };
}
